import React from 'react';
import { useParams } from 'react-router-dom';
import styled from 'styled-components';
import * as yup from 'yup';

import AdminFormPage from 'components/admin/shared/AdminFormPage';
import Button from 'components/shared/Button';
import ButtonGroup from 'components/shared/ButtonGroup';
import CircularProgress from 'components/shared/CircularProgress';
import Checkbox from 'components/shared/form/Checkbox';
import { FormContainer as Form } from 'components/shared/form/Form';
import FormHeader from 'components/shared/form/FormHeader';
import FormSection from 'components/shared/form/FormSection';
import { MultiSelectChips, Select } from 'components/shared/form/InfiniteScrollDropdown';
import Input from 'components/shared/form/Input';
import InputGroup from 'components/shared/form/InputGroup';
import SectionHeader from 'components/shared/form/SectionHeader';
import { useProfile } from 'context/profile';
import { useAdminForm } from 'hooks/datasource';
import useInfiniteOptions from 'hooks/useInfiniteOptions';
import { PERMISSIONS } from 'models/flexible/Profile';
import { QuestionKind, QuestionTemplateType, QuestionType } from 'models/flexible/QuestionTemplate';
import { adminIndexClassifications } from 'services/api/admin/classifications';
import { adminIndexClients } from 'services/api/admin/clients';
import { indexGroupTypes } from 'services/api/admin/groupTypes';
import {
  adminCreateQuestionTemplate,
  adminQuestionTemplatesQueryKeys,
  adminShowQuestionTemplate,
  adminUpdateQuestionTemplate,
} from 'services/api/admin/questionTemplates';
import { useToastActions } from 'stores/toastStore';
import { colors } from 'styles/theme/colors';
import TrashIcon from 'svg/TrashIcon';

// ---------------------------------------------------------------------------
// Constants + helpers
// ---------------------------------------------------------------------------

const getId = (o: any) => o!.id as string;
const getName = (o: any) => o!.name as string;
const getDisplayName = (o: any) => o!.displayName as string;

const PROGRESS_QUESTION_TYPES = [
  { label: 'Yes/No', value: QuestionKind.YES_NO },
  { label: 'Scale 1-10', value: QuestionKind.RANGE },
  { label: 'Numeric', value: QuestionKind.NUMERIC },
  { label: 'Status', value: QuestionKind.STATUS },
];

const DISCHARGE_QUESTION_TYPES = [
  { label: '1-5 Satisfaction Scale', value: QuestionKind.SATISFACTION_SCALE },
  { label: 'Notes', value: QuestionKind.NOTES },
  { label: 'Yes/No', value: QuestionKind.YES_NO },
];

const TEMPLATE_TYPE_OPTIONS = [
  { label: 'Progress Template', value: QuestionTemplateType.PROGRESS },
  { label: 'Discharge Template', value: QuestionTemplateType.DISCHARGE },
];

const RATING_OPTIONS = ['1', '2', '3', '4', '5'].map((r) => ({ label: r, value: r }));

// ---------------------------------------------------------------------------
// Question type helpers
// ---------------------------------------------------------------------------

function newQuestion(kind: QuestionKind): QuestionType {
  return {
    id: '',
    kind,
    text: kind === QuestionKind.STATUS ? 'Is the patient discharge plan on track?' : '',
    order: 0,
    config: {},
  };
}

const schema = yup.object({
  type: yup.string().nullable().required('Template type is required'),
  client: yup
    .mixed()
    .nullable()
    .test('client', 'Client is required', (v: any) => !!v?.id),
  questions: yup.array().min(1, 'At least one question is required').max(10, 'Maximum of ten Questions per Template'),
});

function validate(values: any) {
  const errs: Record<string, string> = {};
  (values.questions ?? []).forEach((q: QuestionType, i: number) => {
    if (!q.text) errs[`questions.${i}.text`] = 'Question text is required';
  });
  return errs;
}

// ---------------------------------------------------------------------------
// Individual question inputs
// ---------------------------------------------------------------------------

type QuestionRowProps = {
  question: QuestionType;
  index: number;
  disabled: boolean;
  hasSatisfactionScale: boolean;
  error?: string;
  onBlur?: () => void;
  onChange: (index: number, q: QuestionType) => void;
  onRemove: (index: number) => void;
};

function QuestionRow({
  question,
  index,
  disabled,
  hasSatisfactionScale,
  error,
  onBlur,
  onChange,
  onRemove,
}: QuestionRowProps) {
  const update = (patch: Partial<QuestionType>) => onChange(index, { ...question, ...patch });
  const updateConfig = (patch: Record<string, any>) => update({ config: { ...question.config, ...patch } });

  const cfg = question.config as any;

  let configExtra: React.ReactNode = null;
  let belowRowExtra: React.ReactNode = null;

  if (question.kind === QuestionKind.YES_NO) {
    configExtra = (
      <RadioToggle
        label='Positive Answer'
        value={cfg.positive === 'no' || !cfg.positive ? 'no' : 'yes'}
        onChange={(v) => updateConfig({ positive: v })}
        disabled={disabled}
      />
    );
  }

  if (question.kind === QuestionKind.RANGE || question.kind === QuestionKind.NUMERIC) {
    const showChart = !!cfg.chart;
    configExtra = (
      <>
        <RadioToggle
          label='Chart'
          value={showChart ? 'yes' : 'no'}
          onChange={(v) => {
            if (v === 'yes') {
              updateConfig({ chart: { chartName: '', yAxis: '' } });
            } else {
              const { chart: _chart, ...rest } = cfg;
              update({ config: rest });
            }
          }}
          disabled={disabled}
        />
        {showChart && (
          <ChartSection>
            <InputGroup title='Chart Name'>
              <Input
                value={cfg.chart?.chartName ?? ''}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                  updateConfig({ chart: { ...cfg.chart, chartName: e.target.value } })
                }
                placeholder='Type here..'
                disabled={disabled}
              />
            </InputGroup>
            <InputGroup title='Measurement Value (y-axis label)'>
              <Input
                value={cfg.chart?.yAxis ?? ''}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                  updateConfig({ chart: { ...cfg.chart, yAxis: e.target.value } })
                }
                placeholder='Type here...'
                disabled={disabled}
              />
            </InputGroup>
          </ChartSection>
        )}
      </>
    );
  }

  if (question.kind === QuestionKind.NOTES) {
    const isOptional = !!cfg.optional;
    configExtra = (
      <RadioToggle
        label='Optional?'
        value={isOptional ? 'yes' : 'no'}
        onChange={(v) => updateConfig({ optional: v === 'yes' })}
        disabled={disabled}
      />
    );
    if (!isOptional && hasSatisfactionScale) {
      belowRowExtra = (
        <SubSection>
          <InputGroup>
            <MultiSelectChips
              title='Select which ratings require a note'
              options={RATING_OPTIONS}
              values={cfg.requiredRatings?.map((r: any) => (typeof r === 'object' ? r : { label: r, value: r })) ?? []}
              onChange={(v) => updateConfig({ requiredRatings: v })}
              getOptionValue={(o) => (o as any).value}
              getOptionLabel={(o) => (o as any).label}
              placeholder='All ratings'
              disabled={disabled}
            />
          </InputGroup>
        </SubSection>
      );
    }
  }

  const kindLabel =
    [...PROGRESS_QUESTION_TYPES, ...DISCHARGE_QUESTION_TYPES].find((o) => o.value === question.kind)?.label ??
    question.kind;

  return (
    <QuestionContainer>
      <QuestionRow2>
        <div style={{ flex: 1 }}>
          <InputGroup title={kindLabel} error={error}>
            <Input
              value={question.text}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => update({ text: e.target.value })}
              placeholder={
                question.kind === QuestionKind.STATUS ? 'Type status prompt here...' : 'Type question here...'
              }
              disabled={disabled}
              $hasError={!!error}
              onBlur={onBlur}
            />
          </InputGroup>
        </div>
        {configExtra}
        <TrashContainer>
          <TrashIcon
            color={disabled ? colors.black25 : colors.accentRed}
            width={20}
            height={20}
            onClick={disabled ? undefined : () => onRemove(index)}
            style={{ cursor: disabled ? 'not-allowed' : 'pointer' }}
          />
        </TrashContainer>
      </QuestionRow2>
      {belowRowExtra}
    </QuestionContainer>
  );
}

// ---------------------------------------------------------------------------
// RadioToggle helper
// ---------------------------------------------------------------------------

function RadioToggle({
  label,
  value,
  onChange,
  disabled,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  disabled?: boolean;
}) {
  return (
    <RadioToggleContainer>
      <RadioLabel>{label}</RadioLabel>
      <RadioOptions>
        {['yes', 'no'].map((opt) => (
          <label key={opt} style={{ display: 'flex', alignItems: 'center', gap: 4, cursor: 'pointer' }}>
            <input
              type='radio'
              value={opt}
              checked={value === opt}
              onChange={() => onChange(opt)}
              disabled={disabled}
            />
            {opt.charAt(0).toUpperCase() + opt.slice(1)}
          </label>
        ))}
      </RadioOptions>
    </RadioToggleContainer>
  );
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export default function EditQuestionTemplate() {
  const { id, type } = useParams<{ id?: string; type?: string }>();
  const isEditing = !!id;
  const { addToast } = useToastActions();
  const { profile } = useProfile();
  const canEdit = profile.has(PERMISSIONS.adminQuestionTemplateEdit);
  const disabled = !canEdit;

  // --- Data + form ---

  const { values, errors, valid, dirty, setValue, validateField, validateForm, submit, loading, goBack } = useAdminForm(
    {
      id,
      show: adminShowQuestionTemplate,
      create: async (payload: any) => {
        try {
          return await adminCreateQuestionTemplate(payload);
        } catch (err: any) {
          const message = err?.response?.data?.message || 'There was an error saving the template.';
          addToast({ text: Array.isArray(message) ? message.join('\n') : message });
          throw err;
        }
      },
      update: async (updateId: string, payload: any) => {
        try {
          return await adminUpdateQuestionTemplate(updateId, payload);
        } catch (err: any) {
          const message = err?.response?.data?.message || 'There was an error saving the template.';
          addToast({ text: Array.isArray(message) ? message.join('\n') : message });
          throw err;
        }
      },
      include: [] as const,
      queryKey: adminQuestionTemplatesQueryKeys,
      schema,
      validate,
      onCreateSuccess: { toast: 'Template created successfully.', navigate: `/admin/question-templates/${type}` },
      onUpdateSuccess: { toast: 'Template updated successfully.', navigate: `/admin/question-templates/${type}` },
      toFormValues: (data) => ({
        id: data?.id ?? '',
        type: data?.type ?? null,
        client: data?.client ?? null,
        planTypeClassifications: data?.planTypeClassifications ?? [],
        episodeClassifications: data?.episodeClassifications ?? [],
        groupTypes: data?.groupTypes ?? [],
        trackLos: data?.trackLos ?? true,
        instructions: data?.instructions ?? '',
        questions: data?.questions ?? [],
      }),
      toPayload: (v) => ({
        clientId: v.client?.id ?? '',
        instructions: v.instructions ?? '',
        type: v.type,
        trackLos: v.trackLos ?? true,
        episodeClassificationIds: (v.episodeClassifications ?? []).map((c: any) => c.id),
        planTypeClassificationIds: (v.planTypeClassifications ?? []).map((c: any) => c.id),
        groupTypeIds: (v.groupTypes ?? []).map((g: any) => g.id),
        questions: (v.questions ?? []).map((q: any, i: number) => {
          const question = { ...q, order: i + 1 };
          if (question.kind === QuestionKind.NOTES && question.config?.requiredRatings?.length) {
            question.config = {
              ...question.config,
              requiredRatings: question.config.optional
                ? []
                : question.config.requiredRatings.map((r: any) => (typeof r === 'object' ? r.value : r)),
            };
          }
          return question;
        }),
      }),
    }
  );

  // --- Dropdown options ---

  const clientOpts = useInfiniteOptions({
    queryKey: ['adminClients', 'questionTemplates'],
    fetchFn: adminIndexClients,
    params: { sortBy: 'name asc' },
  });

  const planTypeOpts = useInfiniteOptions({
    queryKey: ['adminClassifications', 'planType', values?.client?.id],
    fetchFn: adminIndexClassifications,
    params: { type: 'plan_type', client: values?.client?.id, sortBy: 'name asc' },
  });

  const episodeOpts = useInfiniteOptions({
    queryKey: ['adminClassifications', 'episode', values?.client?.id],
    fetchFn: adminIndexClassifications,
    params: { type: 'episode', client: values?.client?.id, sortBy: 'name asc' },
  });

  const groupTypeOpts = useInfiniteOptions({
    queryKey: ['groupTypes', 'questionTemplates', values?.client?.id],
    fetchFn: indexGroupTypes,
    params: { client: values?.client?.id, sortBy: 'displayName asc' },
  });

  const isProgressTemplate = values?.type === QuestionTemplateType.PROGRESS;
  const isDefaultTemplate =
    isEditing &&
    !values?.planTypeClassifications?.length &&
    !values?.episodeClassifications?.length &&
    !values?.groupTypes?.length;
  const questionTypeOptions = isProgressTemplate ? PROGRESS_QUESTION_TYPES : DISCHARGE_QUESTION_TYPES;
  const hasSatisfactionScale = (values?.questions ?? []).some((q) => q.kind === QuestionKind.SATISFACTION_SCALE);

  const handleClientChange = (client: { id: string; name: string } | null) => {
    setValue('client', client);
    setValue('planTypeClassifications', []);
    setValue('episodeClassifications', []);
    setValue('groupTypes', []);
  };

  const handleAddQuestion = (opt: { label: string; value: QuestionKind } | null) => {
    if (!opt) return;
    setValue('questions', [...(values?.questions ?? []), newQuestion(opt.value)]);
  };

  const handleQuestionChange = (index: number, q: QuestionType) => {
    const updated = [...(values?.questions ?? [])];
    updated[index] = q;
    setValue('questions', updated);
  };

  const handleRemoveQuestion = (index: number) => {
    setValue(
      'questions',
      (values?.questions ?? []).filter((_, i) => i !== index)
    );
  };

  // --- Render ---

  if (isEditing && loading) return <CircularProgress />;

  return (
    <AdminFormPage>
      <FormHeader title={isEditing ? 'Edit Template' : 'Add Template'} />
      <Form>
        <FormSection>
          <SectionHeader>Template Information</SectionHeader>

          <InputGroup title='Template Type' error={errors?.type}>
            {isEditing ? (
              <Input value={isProgressTemplate ? 'Progress Template' : 'Discharge Template'} disabled />
            ) : (
              <Select
                options={TEMPLATE_TYPE_OPTIONS}
                value={TEMPLATE_TYPE_OPTIONS.find((o) => o.value === values?.type) ?? null}
                onChange={(opt) => {
                  setValue('type', opt?.value ?? null);
                  setValue('questions', [], { validate: false });
                }}
                onBlur={() => validateField('type')}
                getOptionValue={(o) => o.value}
                getOptionLabel={(o) => o.label}
                placeholder='Select template type'
                disabled={disabled}
                hasError={!!errors?.type}
              />
            )}
          </InputGroup>

          <InputGroup title='Client' error={errors?.client}>
            <Select
              {...clientOpts}
              value={values?.client ?? null}
              onChange={handleClientChange}
              onBlur={() => validateField('client')}
              getOptionValue={getId}
              getOptionLabel={getName}
              placeholder='Select client'
              isDisabled={isEditing || disabled}
              hasError={!!errors?.client}
            />
          </InputGroup>

          <InputGroup>
            <MultiSelectChips
              title='Plan Types'
              {...planTypeOpts}
              values={values?.planTypeClassifications ?? []}
              onChange={(v) => setValue('planTypeClassifications', v)}
              getOptionValue={getId}
              getOptionLabel={getName}
              placeholder='Select plan types'
              disabled={!values?.client?.id || isDefaultTemplate || disabled}
            />
          </InputGroup>

          <InputGroup>
            <MultiSelectChips
              title='Episode Types'
              {...episodeOpts}
              values={values?.episodeClassifications ?? []}
              onChange={(v) => setValue('episodeClassifications', v)}
              getOptionValue={getId}
              getOptionLabel={getName}
              placeholder='Select episode types'
              disabled={!values?.client?.id || isDefaultTemplate || disabled}
            />
          </InputGroup>

          <InputGroup>
            <MultiSelectChips
              title='Managed Care Options (select all that apply)'
              {...groupTypeOpts}
              values={values?.groupTypes ?? []}
              onChange={(v) => setValue('groupTypes', v)}
              getOptionValue={getId}
              getOptionLabel={getDisplayName}
              placeholder='Select care options'
              disabled={!values?.client?.id || isDefaultTemplate || disabled}
            />
          </InputGroup>

          {isProgressTemplate && (
            <div style={{ marginBottom: 28 }}>
              <Checkbox
                label='Do not track days for this template'
                labelSize='14px'
                checked={!values?.trackLos}
                onChange={(checked: boolean) => setValue('trackLos', !checked)}
                disabled={isEditing || disabled}
              />
            </div>
          )}

          <InputGroup title='Instructions (optional)'>
            <Input
              value={values?.instructions ?? ''}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setValue('instructions', e.target.value)}
              placeholder='Instructions'
              disabled={disabled}
            />
          </InputGroup>
        </FormSection>

        <FormSection>
          <SectionHeader>Template Questions</SectionHeader>

          <InputGroup>
            <AddQuestionSelect>
              <Select
                options={questionTypeOptions}
                value={null}
                onChange={handleAddQuestion}
                onBlur={() => validateField('questions')}
                getOptionValue={(o) => o!.value}
                getOptionLabel={(o) => o!.label}
                placeholder='Add Question'
                isDisabled={disabled}
              />
            </AddQuestionSelect>
          </InputGroup>

          {errors?.questions && <ErrorText>{errors.questions}</ErrorText>}

          {(values?.questions ?? []).map((question, index) => (
            <QuestionRow
              key={question.id || `new-${index}`}
              question={question}
              index={index}
              disabled={disabled}
              hasSatisfactionScale={hasSatisfactionScale}
              error={errors?.[`questions.${index}.text`]}
              onBlur={validateForm}
              onChange={handleQuestionChange}
              onRemove={handleRemoveQuestion}
            />
          ))}
        </FormSection>

        <ButtonGroup>
          <Button variant='ghost' onClick={goBack}>
            Cancel
          </Button>
          <Button onClick={submit} disabled={disabled || !dirty || !valid}>
            {isEditing ? 'Update Template' : 'Create Template'}
          </Button>
        </ButtonGroup>
      </Form>
    </AdminFormPage>
  );
}

// ---------------------------------------------------------------------------
// Styled
// ---------------------------------------------------------------------------

const AddQuestionSelect = styled.div`
  .css-190ckys,
  [class*='-control'] {
    border-color: ${colors.primaryBlue} !important;
  }
  [class*='Placeholder'] {
    color: ${colors.primaryBlue} !important;
  }
  [class*='indicatorSeparator'] {
    background-color: ${colors.primaryBlue} !important;
  }
  [class*='indicatorContainer'] svg,
  [class*='DropdownIndicator'] svg {
    color: ${colors.primaryBlue} !important;
    fill: ${colors.primaryBlue} !important;
  }
`;

const QuestionContainer = styled.div`
  margin-bottom: 16px;
`;

const QuestionRow2 = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 16px;
  & > *:first-child {
    flex: 1;
  }
`;

const TrashContainer = styled.div`
  display: flex;
  align-items: center;
  padding-top: 28px;
`;

const ChartSection = styled.div`
  margin-left: 40px;
  display: flex;
  gap: 16px;
  & > * {
    flex: 1;
  }
`;

const SubSection = styled.div`
  margin-left: 40px;
`;

const RadioToggleContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const RadioLabel = styled.div`
  font-size: 12px;
  font-weight: 600;
  color: ${colors.black50};
`;

const RadioOptions = styled.div`
  display: flex;
  gap: 12px;
  padding-top: 8px;
`;

const ErrorText = styled.p`
  color: ${colors.accentRed};
  font-size: 14px;
  margin: 0 0 8px;
`;
